import { Mic, Volume2 } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import Wave from "../../templates/Wave";
import CountdownTimer from "../../templates/CountdownTimer";
import TextToSpeech from "../../templates/TextToSpeech";

function ListenAssessment({ timeLeft, setTimeLeft, response, Exit }) {
  const [index, setIndex] = useState(0);
  const [started, setStarted] = useState("intro");
  const [finished, setFinished] = useState(false);

  const recognitionRef = useRef(null);
  const [play, setPlay] = useState(false);

  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.lang = "en-US";
      recognition.interimResults = false;
      recognition.continuous = false;

      recognition.onerror = (event) => {
        console.error("Recognition error:", event.error);
      };

      recognitionRef.current = recognition;
    } else {
      console.error("SpeechRecognition not supported in this browser.");
    }
  }, []);

  const handlePlayNext = () => {
    if (index < response.length) {
      setPlay(true);
    } else {
      setFinished(true);
    }
  };

  useEffect(() => {
    if (timeLeft === 0) {
      setFinished(true);
    }
  }, [timeLeft]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-br from-gray-100 to-gray-200">
      {started && !finished && (
        <>
          {started === "intro" && (
            <div className="bg-white rounded-2xl shadow-2xl p-10 max-w-2xl border border-gray-200 text-center">
              <h1 className="text-3xl font-bold text-gray-800 mb-6">
                🎧 Welcome to Listening Assessment
              </h1>
              <p className="text-3xl  text-gray-800 mb-6">
                In this section you will listen a sentence, you need to repeat
                the sentence to complete listening assessment. Click on Play
                Sentence button to listen to the statement each time until the
                section is completed.
              </p>
              <TextToSpeech
                speech="In this section you will listen a sentence, you need to repeat the sentence to complete listening assessment. Click on Play Sentence button to listen to the statement each time until the section is completed."
                onEnd={() => setStarted("ready")}
              />
            </div>
          )}

          {started === "ready" && (
            <div className="w-full max-w-3xl">
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-700">
                  Section B
                </h2>
                <CountdownTimer
                  label="Time Remaining"
                  timeLeft={timeLeft}
                  setTimeLeft={setTimeLeft}
                />
              </div>

              {/* Card */}
              <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-200">
                {/* Play sentence row */}
                <div className="flex items-center justify-center gap-4 mb-6">
                  <button
                    onClick={handlePlayNext}
                    className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 transition shadow-md"
                  >
                    <Volume2 className="text-white" size={24} />
                    Play Sentence
                  </button>
                </div>

                {play && (
                  <TextToSpeech
                    key={index}
                    speech={response[index]}
                    onEnd={() => {
                      setPlay(false);
                      setIndex((prev) => prev + 1);
                    }}
                  />
                )}

                {/* Mic row */}
                <div className="flex flex-col items-center space-y-4 bg-gray-50 rounded-xl border-2 border-gray-200 p-6 mb-6">
                  <div className="w-20 h-20 flex items-center justify-center bg-green-500 text-white rounded-full shadow-lg text-3xl animate-pulse">
                    <Mic size={28} />
                  </div>
                  <Wave color="green" />
                  <p className="text-gray-600 font-medium">
                    Repeat what you hear
                  </p>
                </div>

                {/* Footer */}
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setFinished(true)}
                    className="px-6 py-2 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-300 transition shadow-md"
                  >
                    Next Section
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Finished Modal */}
      {finished && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-8 rounded-2xl max-w-md w-full shadow-xl text-center space-y-6">
            {timeLeft !== 0 && (
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setFinished(false);
                    setStarted("ready");
                  }}
                  className="text-gray-500 hover:text-gray-900 text-2xl"
                >
                  ✕
                </button>
              </div>
            )}

            <p className="text-lg font-medium text-gray-800">
              🎉 You have completed Listening Assessment.
              <br /> Click <strong>Next</strong> to proceed.
            </p>
            <button
              type="button"
              onClick={Exit}
              className="px-6 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 transition"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ListenAssessment;
